// src/views/EmpleadosView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { exportEmpleadosDirectorioPdf } from "../utils/exportEmpleadosDirectorioPdf";
import { getEmpleados, deleteEmpleado } from "../services/api";

function normalize(v) {
  return (v ?? "").toString().trim();
}

function money(v) {
  if (v === null || v === undefined || v === "") return "$ ******";

  const n = Number(v);
  if (Number.isNaN(n)) return "$ ******";

  return n.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
  });
}

function initials(nombre = "") {
  const parts = normalize(nombre).split(/\s+/).filter(Boolean);

  if (!parts.length) return "NA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function getEstatus(e) {
  return normalize(e.estatus || e.status || "ACTIVO").toUpperCase();
}

function isActivo(e) {
  return getEstatus(e) === "ACTIVO";
}

function getId(e) {
  return e.id || e.empleadoId || e.pk || e.uuid;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function filenameStamp() {
  const d = new Date();

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate()
  )}_${pad2(d.getHours())}${pad2(d.getMinutes())}`;
}

function escapeCsvCell(v) {
  const s = normalize(v);

  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }

  return s;
}

function downloadBlob(content, mimeType, filename) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

export default function EmpleadosView() {
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [query, setQuery] = useState("");
  const [filterEstatus, setFilterEstatus] = useState("TODOS");
  const [filterRol, setFilterRol] = useState("TODOS");
  const [filterArea, setFilterArea] = useState("TODOS");

  const [showSalaries, setShowSalaries] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const cargarEmpleados = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await getEmpleados();
      setEmpleados(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error al cargar empleados:", err);
      setErrorMsg("No se pudo cargar el directorio de empleados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarEmpleados();
  }, []);

  const roles = useMemo(() => {
    const set = new Set(empleados.map((e) => normalize(e.rol)).filter(Boolean));

    return ["TODOS", ...Array.from(set).sort((a, b) => a.localeCompare(b, "es"))];
  }, [empleados]);

  const areas = useMemo(() => {
    const set = new Set(empleados.map((e) => normalize(e.area)).filter(Boolean));

    return ["TODOS", ...Array.from(set).sort((a, b) => a.localeCompare(b, "es"))];
  }, [empleados]);

  const filtered = useMemo(() => {
    const q = normalize(query).toLowerCase();

    return empleados
      .filter((e) => {
        if (filterEstatus !== "TODOS" && getEstatus(e) !== filterEstatus) {
          return false;
        }

        if (filterRol !== "TODOS" && normalize(e.rol) !== filterRol) {
          return false;
        }

        if (filterArea !== "TODOS" && normalize(e.area) !== filterArea) {
          return false;
        }

        if (!q) return true;

        const haystack = [
          e.nombre,
          e.telefono,
          e.ubicacion,
          e.oficina,
          e.rol,
          e.area,
          e.estatus,
          e.correo,
          e.email,
          e.puesto,
        ]
          .map((x) => normalize(x).toLowerCase())
          .join(" ");

        return haystack.includes(q);
      })
      .sort((a, b) => normalize(a.nombre).localeCompare(normalize(b.nombre), "es"));
  }, [empleados, query, filterEstatus, filterRol, filterArea]);

  const resumen = useMemo(() => {
    const oficinas = new Set(filtered.map((e) => normalize(e.oficina)).filter(Boolean));
    const areasSet = new Set(filtered.map((e) => normalize(e.area)).filter(Boolean));
    const activos = filtered.filter((e) => isActivo(e)).length;
    const totalNomina = filtered.reduce((acc, e) => acc + (Number(e.sueldo) || 0), 0);

    return {
      total: filtered.length,
      activos,
      inactivos: filtered.length - activos,
      oficinas: oficinas.size,
      areas: areasSet.size,
      totalNomina,
    };
  }, [filtered]);

  const eliminar = async (empleado) => {
    const id = getId(empleado);

    if (!id) {
      alert("No se encontró el ID del empleado.");
      return;
    }

    const ok = window.confirm(
      `¿Eliminar a ${empleado.nombre || "este empleado"}? Esta acción no se puede deshacer.`
    );

    if (!ok) return;

    try {
      setDeletingId(id);
      await deleteEmpleado(id);
      await cargarEmpleados();

      if (selectedEmpleado && getId(selectedEmpleado) === id) {
        setSelectedEmpleado(null);
      }
    } catch (err) {
      console.error("Error al eliminar empleado:", err);
      alert("Error al eliminar empleado.");
    } finally {
      setDeletingId(null);
    }
  };

  const limpiarFiltros = () => {
    setQuery("");
    setFilterEstatus("TODOS");
    setFilterRol("TODOS");
    setFilterArea("TODOS");
  };

  const exportCsv = () => {
    if (!filtered.length) return alert("No hay empleados para exportar.");

    const headers = [
      "Nombre",
      "Teléfono",
      "Correo",
      "Ubicación",
      "Oficina",
      "Rol",
      "Área",
      "Estatus",
      "Sueldo",
    ];

    const lines = [];

    lines.push(["Reporte", "Directorio de Empleados"].map(escapeCsvCell).join(","));
    lines.push(["Generado", new Date().toLocaleString("es-MX")].map(escapeCsvCell).join(","));
    lines.push(["Registros", String(filtered.length)].map(escapeCsvCell).join(","));
    lines.push("");
    lines.push(headers.map(escapeCsvCell).join(","));

    filtered.forEach((e) => {
      const row = [
        e.nombre || "-",
        e.telefono || "-",
        e.correo || e.email || "-",
        e.ubicacion || "-",
        e.oficina || "-",
        e.rol || "-",
        e.area || "-",
        getEstatus(e),
        showSalaries ? money(e.sueldo) : "OCULTO",
      ];

      lines.push(row.map(escapeCsvCell).join(","));
    });

    const csvContent = "\uFEFF" + lines.join("\n");

    downloadBlob(csvContent, "text/csv;charset=utf-8;", `empleados_${filenameStamp()}.csv`);
  };

const exportPdf = () => {
  exportEmpleadosDirectorioPdf({
    empleados: filtered,
    resumen,
    showSalaries,
  });
};
  const MiniKpi = ({ label, value, sub }) => (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-xl font-black text-slate-950">{value}</div>

      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );

  const StatusBadge = ({ empleado }) => {
    const activo = isActivo(empleado);

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-black ${
          activo
            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
            : "bg-rose-50 text-rose-700 border border-rose-100"
        }`}
      >
        {activo ? "ACTIVO" : getEstatus(empleado)}
      </span>
    );
  };

  const EmployeeAvatar = ({ empleado, size = "md" }) => {
    const sizeClass = size === "lg" ? "h-16 w-16 text-xl" : "h-11 w-11 text-sm";

    if (empleado.fotoUrl) {
      return (
        <img
          src={empleado.fotoUrl}
          alt={empleado.nombre || "Empleado"}
          className={`${sizeClass} rounded-2xl object-cover border border-slate-200`}
        />
      );
    }

    return (
      <div
        className={`${sizeClass} rounded-2xl bg-gradient-to-br from-slate-950 via-indigo-800 to-violet-700 text-white flex items-center justify-center font-black shadow-sm`}
      >
        {initials(empleado.nombre)}
      </div>
    );
  };

  const Info = ({ label, value }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide font-black text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-sm font-bold text-slate-900 break-words">
        {normalize(value) || "-"}
      </div>
    </div>
  );

  const EmployeeProfilePanel = () => {
    if (!selectedEmpleado) return null;

    const e = selectedEmpleado;
    const id = getId(e);

    return (
      <div className="fixed inset-0 z-50">
        <div
          className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
          onClick={() => setSelectedEmpleado(null)}
        />

        <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl p-6 overflow-y-auto">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <EmployeeAvatar empleado={e} size="lg" />

              <div>
                <h2 className="text-2xl font-black text-slate-950">
                  {e.nombre || "Sin nombre"}
                </h2>

                <div className="mt-1 text-slate-600">
                  {e.rol || "Sin rol"} · {e.area || "Sin área"}
                </div>

                <div className="mt-2">
                  <StatusBadge empleado={e} />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setSelectedEmpleado(null)}
              className="h-10 w-10 rounded-xl border border-slate-200 hover:bg-slate-50 font-black"
            >
              ×
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Info label="Teléfono" value={e.telefono} />
            <Info label="Correo" value={e.correo || e.email} />
            <Info label="Ubicación" value={e.ubicacion} />
            <Info label="Oficina" value={e.oficina} />
            <Info label="Rol" value={e.rol} />
            <Info label="Área" value={e.area} />
            <Info label="Sueldo" value={showSalaries ? money(e.sueldo) : "$ ******"} />
            <Info label="Estatus" value={getEstatus(e)} />
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="text-sm font-black text-slate-900">Lectura rápida</div>

            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              {e.nombre || "El empleado"} está registrado como{" "}
              <strong>{e.rol || "sin rol"}</strong> en el área{" "}
              <strong>{e.area || "sin área"}</strong>, adscrito a la oficina{" "}
              <strong>{e.oficina || "sin oficina"}</strong> en{" "}
              <strong>{e.ubicacion || "sin ubicación"}</strong>.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/empleados/editar/${id}`}
              className="px-5 py-3 rounded-xl bg-indigo-700 text-white font-black hover:bg-indigo-800"
            >
              Editar empleado
            </Link>

            <Link
              to="/empleados/organigrama"
              className="px-5 py-3 rounded-xl border border-slate-200 bg-white font-black hover:bg-slate-50"
            >
              Ver organigrama
            </Link>

            <button
              type="button"
              onClick={() => eliminar(e)}
              className="px-5 py-3 rounded-xl border border-red-100 bg-red-50 text-red-700 font-black hover:bg-red-100"
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const EmployeeTable = () => (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-[1180px] w-full text-sm">
        <thead>
          <tr className="bg-slate-950 text-white">
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Empleado
            </th>
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Área / Rol
            </th>
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Ubicación
            </th>
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Oficina
            </th>
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Contacto
            </th>
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Sueldo
            </th>
            <th className="px-5 py-4 text-left text-[11px] font-black uppercase tracking-[0.14em]">
              Estatus
            </th>
            <th className="px-5 py-4 text-right text-[11px] font-black uppercase tracking-[0.14em]">
              Acciones
            </th>
          </tr>
        </thead>

        <tbody>
          {filtered.map((e, idx) => {
            const id = getId(e);

            return (
              <tr
                key={id}
                className={`border-b last:border-b-0 ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                } hover:bg-indigo-50/40 transition`}
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <EmployeeAvatar empleado={e} />

                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => setSelectedEmpleado(e)}
                        className="font-black text-slate-950 hover:text-indigo-700 text-left truncate block max-w-[280px]"
                      >
                        {e.nombre || "-"}
                      </button>

                      <div className="mt-1 text-xs text-slate-500">
                        ID: {id ? String(id).slice(0, 8) : "SIN-ID"}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4">
                  <div className="font-black text-slate-900">{e.area || "Sin área"}</div>
                  <div className="mt-1 text-xs text-slate-500">{e.rol || "Sin rol"}</div>
                </td>

                <td className="px-5 py-4 text-slate-700">
                  {e.ubicacion || "-"}
                </td>

                <td className="px-5 py-4 text-slate-700">
                  {e.oficina || "-"}
                </td>

                <td className="px-5 py-4">
                  <div className="font-bold text-slate-800">{e.telefono || "-"}</div>
                  <div className="mt-1 text-xs text-slate-500 truncate max-w-[180px]">
                    {e.correo || e.email || "-"}
                  </div>
                </td>

                <td className="px-5 py-4">
                  <div className="inline-flex items-center rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 font-black text-slate-900">
                    {showSalaries ? money(e.sueldo) : "$ ******"}
                  </div>
                </td>

                <td className="px-5 py-4">
                  <StatusBadge empleado={e} />
                </td>

                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedEmpleado(e)}
                      className="px-3 py-2 rounded-xl bg-slate-950 text-white font-black hover:bg-indigo-700 transition"
                    >
                      Ver
                    </button>

                    <Link
                      to={`/empleados/editar/${id}`}
                      className="px-3 py-2 rounded-xl border border-slate-200 bg-white font-black hover:bg-slate-50 transition"
                    >
                      Editar
                    </Link>

                    <button
                      type="button"
                      onClick={() => eliminar(e)}
                      disabled={deletingId === id}
                      className="px-3 py-2 rounded-xl border border-red-100 bg-red-50 text-red-700 font-black hover:bg-red-100 disabled:opacity-50 transition"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1500px] mx-auto px-6 py-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-5">
          <div>
            <div className="text-xs font-black tracking-[0.25em] text-slate-500 uppercase">
              Gestión interna
            </div>

            <h1 className="mt-2 text-3xl font-black text-slate-950">
              Directorio de Empleados
            </h1>

            <p className="mt-2 text-sm text-slate-500">
              Directorio corporativo para consultar personal, ubicación, oficina, rol y estatus.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Link
              to="/empleados/organigrama"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 font-black shadow-sm hover:bg-slate-100 transition"
            >
              👥 Organigrama
            </Link>

            <Link
              to="/empleados/nuevo"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-indigo-800 text-white font-black shadow-lg shadow-indigo-900/20 hover:bg-indigo-900 transition"
            >
              + Nuevo empleado
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
          <MiniKpi label="Empleados" value={resumen.total} sub="Filtrados" />
          <MiniKpi label="Activos" value={resumen.activos} sub="Vigentes" />
          <MiniKpi label="Inactivos" value={resumen.inactivos} sub="Bajas o suspendidos" />
          <MiniKpi label="Oficinas" value={resumen.oficinas} sub="Cobertura" />
          <MiniKpi label="Áreas" value={resumen.areas} sub="Estructura" />
          <MiniKpi
            label="Nómina"
            value={showSalaries ? money(resumen.totalNomina) : "$ ******"}
            sub={showSalaries ? "Visible" : "Oculta"}
          />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_170px_170px_170px] gap-3">
              <div className="relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar empleado, teléfono, oficina, área o rol..."
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                />

                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                  🔎
                </span>
              </div>

              <select
                value={filterEstatus}
                onChange={(e) => setFilterEstatus(e.target.value)}
                className="px-4 py-3 rounded-xl border border-slate-200 bg-white font-bold"
              >
                <option value="TODOS">Estatus</option>
                <option value="ACTIVO">Activos</option>
                <option value="INACTIVO">Inactivos</option>
              </select>

              <select
                value={filterRol}
                onChange={(e) => setFilterRol(e.target.value)}
                className="px-4 py-3 rounded-xl border border-slate-200 bg-white font-bold"
              >
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r === "TODOS" ? "Roles" : r}
                  </option>
                ))}
              </select>

              <select
                value={filterArea}
                onChange={(e) => setFilterArea(e.target.value)}
                className="px-4 py-3 rounded-xl border border-slate-200 bg-white font-bold"
              >
                {areas.map((a) => (
                  <option key={a} value={a}>
                    {a === "TODOS" ? "Áreas" : a}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-2 justify-start xl:justify-end">
              <button
                type="button"
                onClick={() => setShowSalaries((v) => !v)}
                className={`px-4 py-3 rounded-xl font-black border ${
                  showSalaries
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-slate-950 text-white border-slate-950"
                }`}
              >
                {showSalaries ? "Ocultar sueldos" : "Ver sueldos"}
              </button>

              <button
                type="button"
                onClick={exportCsv}
                className="px-4 py-3 rounded-xl bg-green-600 text-white font-black hover:bg-green-700"
              >
                Excel
              </button>

              <button
                type="button"
                onClick={exportPdf}
                className="px-4 py-3 rounded-xl bg-red-600 text-white font-black hover:bg-red-700"
              >
                PDF
              </button>
            </div>
          </div>

          {(query || filterEstatus !== "TODOS" || filterRol !== "TODOS" || filterArea !== "TODOS") && (
            <div className="mt-3">
              <button
                type="button"
                onClick={limpiarFiltros}
                className="text-sm font-bold text-indigo-700 hover:text-indigo-900"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>

        {errorMsg && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 font-bold">
            {errorMsg}
          </div>
        )}

        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-black text-slate-950">
              Personal registrado
            </h2>

            <p className="text-xs text-slate-500">
              {filtered.length} empleado{filtered.length === 1 ? "" : "s"} encontrado
              {filtered.length === 1 ? "" : "s"}.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 font-black">
              {resumen.activos} activos
            </span>

            <span className="rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-3 py-1 font-black">
              {resumen.inactivos} inactivos
            </span>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-20 border-b last:border-b-0 border-slate-100 animate-pulse bg-white"
              >
                <div className="h-full flex items-center px-5 gap-4">
                  <div className="h-11 w-11 rounded-2xl bg-slate-100" />
                  <div className="flex-1">
                    <div className="h-3 w-48 bg-slate-100 rounded mb-2" />
                    <div className="h-3 w-28 bg-slate-100 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
            <div className="text-xl font-black text-slate-950">
              No hay empleados para mostrar
            </div>

            <div className="mt-2 text-slate-500">
              Prueba cambiando filtros o registra un nuevo empleado.
            </div>
          </div>
        ) : (
          <EmployeeTable />
        )}

        <EmployeeProfilePanel />
      </div>
    </div>
  );
}